import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useCallback, useEffect, useRef, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SITE_URL } from "@/lib/site-url";
import { AuthProvider } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";
import { ThemeProvider } from "@/lib/theme";
import { CurrencyProvider } from "@/lib/currency";
import { Toaster } from "@/components/ui/sonner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "SmsEmpire — Instant SMS Verification & Disposable Email" },
      {
        name: "description",
        content:
          "Receive SMS codes online in seconds, generate disposable emails on Gmail, Outlook, iCloud & Yandex, and buy verified accounts. Trusted by 50,000+ users worldwide.",
      },
      { name: "author", content: "SmsEmpire" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { name: "theme-color", content: "#4f46e5" },
      { name: "format-detection", content: "telephone=no" },
      { name: "application-name", content: "SmsEmpire" },
      { property: "og:site_name", content: "SmsEmpire" },
      { property: "og:title", content: "SmsEmpire — Instant SMS Verification & Disposable Email" },
      {
        property: "og:description",
        content:
          "Receive SMS codes online in seconds, generate disposable emails on Gmail, Outlook, iCloud & Yandex, and buy verified accounts.",
      },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "es_ES" },
      { property: "og:locale:alternate", content: "fr_FR" },
      { property: "og:locale:alternate", content: "de_DE" },
      { property: "og:locale:alternate", content: "it_IT" },
      { property: "og:locale:alternate", content: "pt_PT" },
      { property: "og:locale:alternate", content: "ru_RU" },
      { property: "og:locale:alternate", content: "zh_CN" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@smsempire" },
      { name: "twitter:title", content: "SmsEmpire — Instant SMS Verification & Disposable Email" },
      {
        name: "twitter:description",
        content:
          "SMS codes, disposable emails and verified accounts — instant, private, worldwide.",
      },
      { property: "og:image", content: `${SITE_URL}/og/default.png` },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content: "SmsEmpire — Instant SMS Verification & Disposable Email",
      },
      { name: "twitter:image", content: `${SITE_URL}/og/default.png` },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", href: "/favicon.ico", type: "image/x-icon" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "sitemap", type: "application/xml", href: "/sitemap.xml" },
      { rel: "alternate", type: "application/rss+xml", title: "SmsEmpire Blog", href: "/feed.xml" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "SmsEmpire",
          url: `${SITE_URL}/`,
          logo: {
            "@type": "ImageObject",
            url: `${SITE_URL}/favicon.ico`,
            width: 32,
            height: 32,
          },
          description:
            "SmsEmpire provides instant SMS verification numbers, disposable email inboxes and pre-made verified accounts across 200+ services worldwide.",
          sameAs: ["https://twitter.com/smsempire", "https://t.me/smsempire"],
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer support",
            availableLanguage: [
              "English",
              "Spanish",
              "French",
              "German",
              "Italian",
              "Portuguese",
              "Russian",
              "Chinese",
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "SmsEmpire",
          url: `${SITE_URL}/`,
          applicationCategory: "UtilitiesApplication",
          operatingSystem: "Web",
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "USD",
            lowPrice: "0.35",
            highPrice: "50.00",
            offerCount: "200",
          },
          description:
            "Rent virtual phone numbers to receive SMS one-time codes, generate disposable email inboxes on real domains, and buy pre-made verified accounts.",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

const VALID_LOCALES = ["en", "es", "fr", "de", "it", "pt", "ru", "zh"];

function RootShell({ children }: { children: ReactNode }) {
  // The language of the document, taken from the router's location.
  //
  // This read used to be `typeof window !== "undefined" ? window.location.pathname : ""`,
  // which is exactly backwards for the thing it was written for (SEO-002): the shell
  // renders on the *server*, where there is no `window`, so the pathname was empty on
  // every SSR response and every page in all eight languages went out as
  // `<html lang="en">`. Crawlers were being told the eight localized versions of a page
  // were the same English document — while the hreflang cluster in the head said they
  // were alternates — and Google resolved the contradiction by picking one canonical
  // for the group ("Duplicate, Google chose a different canonical").
  //
  // `useRouterState` knows the location on both sides: `HeadContent`, rendered just
  // below, reads router state the same way.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const pathLang = pathname.split("/")[1];
  const htmlLang = VALID_LOCALES.includes(pathLang) ? pathLang : "en";

  return (
    <html lang={htmlLang}>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

// 🐴 My Little Pony easter egg — enabled from Settings (Profile page)
const MLP_NOTES = [
  261.63,
  293.66,
  329.63,
  349.23,
  392.0,
  440.0,
  493.88, // C4–B4
  392.0,
  349.23,
  329.63,
  293.66,
  261.63, // back down
  329.63,
  392.0,
  440.0,
  493.88,
  523.25, // climb
  493.88,
  440.0,
  392.0,
  349.23,
  329.63,
  293.66,
  261.63, // full descent
  392.0,
  440.0,
  523.25,
  587.33,
  659.25,
  587.33,
  523.25, // high run
  493.88,
  440.0,
  392.0,
  329.63,
  293.66,
  261.63, // finale
];

function useMLP() {
  const noteIdx = useRef(0);
  const audioCtx = useRef<AudioContext | null>(null);

  const play = useCallback(() => {
    if (typeof window === "undefined") return;
    if (!audioCtx.current) {
      audioCtx.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    const ctx = audioCtx.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    const freq = MLP_NOTES[noteIdx.current % MLP_NOTES.length];
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    gain.gain.setValueAtTime(0.18, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.38);
    noteIdx.current = (noteIdx.current + 1) % MLP_NOTES.length;
  }, []);

  useEffect(() => {
    // Only attach the listener when the easter egg is actually on. It used to be
    // registered on every page — including login and the card payment form — and
    // merely returned early inside the handler. A global keydown listener on those
    // screens is not something to ship, however harmless the handler is.
    if (typeof window === "undefined") return;

    const isEnabled = () => localStorage.getItem("mlp_mode") === "1";
    let attached = false;

    const sync = () => {
      if (isEnabled() && !attached) {
        window.addEventListener("keydown", play);
        attached = true;
      } else if (!isEnabled() && attached) {
        window.removeEventListener("keydown", play);
        attached = false;
      }
    };

    sync();
    // The profile page toggles the flag; `storage` covers other tabs, and the custom
    // event covers this one.
    window.addEventListener("storage", sync);
    window.addEventListener("mlp_mode_changed", sync);
    return () => {
      if (attached) window.removeEventListener("keydown", play);
      window.removeEventListener("storage", sync);
      window.removeEventListener("mlp_mode_changed", sync);
    };
  }, [play]);
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useMLP();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <I18nProvider>
          <CurrencyProvider>
            <AuthProvider>
              {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
              <Outlet />
              <Toaster richColors position="top-right" style={{ marginTop: "70px" }} />
            </AuthProvider>
          </CurrencyProvider>
        </I18nProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
